import React, { useState, useEffect, useMemo } from 'react';
import { useAccounts } from '../../context/AccountContext';
import { useTranslation } from "react-i18next";
import { useChangePassword } from '../../hooks/PasswordHooks';
import { usePasswordRules } from '../../hooks/AccountHooks';
import { useViewport } from '../../hooks/useViewport';
import { PasswordParams } from '../../types/PasswordParams';
import { ResultPanel } from '../ResultPanel'
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Card } from '@uhg-abyss/web/ui/Card';
import { HelpMeAlert } from '../HelpMeAlert';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { styled } from '@uhg-abyss/web/tools/styled';
import SelectedAccountInfoBox from '../SelectedAccountInfoBox';

const PasswordRulesPrompt = styled('h6', {
    padding: '0.625rem',
    margin: '0px',
    textAlign: 'center',
    // !important needed: Abyss's own global "h6:not(.abyss-heading-root)" reset
    // otherwise overrides this with its default heading blue.
    color: '$textPrimary !important',
});

const RuleInlineIcon = styled(IconSymbol, {
    marginRight: '8px',
    verticalAlign: 'middle',
});

const ValidationInlineIcon = styled(IconSymbol, {
    marginRight: '8px',
    verticalAlign: 'bottom',
});

const PasswordInputBlock = styled('div', { marginBottom: 12 });
const ConfirmPasswordInputBlock = styled('div', { marginBottom: 8 });

const PasswordTextInput = styled(TextInput, {
    '&.abyss-text-input': {
        border: '1px solid $gray4',
        minHeight: 40,
    },
});

const ValidationList = styled('ul', { listStyleType: 'none', paddingLeft: 0, margin: '8px 0' });

const SubmitButtonBlock = styled('div', { padding: 15, margin: 4 });
const SubmitButton = styled(Button, {
    '&.abyss-button-root': {
        backgroundColor: '$interactiveNav',
        borderColor: '$interactiveNav',
        color: '$textOnDark',
        '&:hover': {
            backgroundColor: '$surfaceHeroHover',
            borderColor: '$surfaceHeroHover',
            color: '$textPrimary',
            boxShadow: '1px 1px 1px #dadbdc',
        },
        '&[aria-disabled="true"]': {
            backgroundColor: '$surfaceDisabled',
            borderColor: '$surfaceDisabled',
            color: '$textDisabled',
            boxShadow: '1px 1px 1px #dadbdc',
        },
    },
});

const minimumLengthDefault = 14;
const countOfOthersDefault = 3;

type CountOfOthersValidation = {
    hasLowerCase: boolean;
    hasUpperCase: boolean;
    hasSpecialCharacter: boolean;
    hasNumber: boolean;
    isValid: boolean;
};

export const NewPassword = () => {
    const { t } = useTranslation();
    const { isTabletOrMobile, isPortrait } = useViewport();
    const warningBody = `${t('newPasswordWarning1')}\n${t('newPasswordWarning2')}`;

    const textInputWidth = isTabletOrMobile
        ? isPortrait
            ? 216
            : 280
        : 360;  //default

    const headingOffset = isTabletOrMobile
        ? 6   //h6 heading
        : 5;  //default, h5 heading

    const cardBorderRadius = isTabletOrMobile ? 5 : 10;
    const cardPadding = isTabletOrMobile ? 5 : 15;
    const textSize = isTabletOrMobile ? "sm" : "md";
    const lineHeight = isTabletOrMobile ? 16 : 24;

    const { user, selectedAccount } = useAccounts();
    const { data: passwordRules } = usePasswordRules(selectedAccount?.resourceId ?? 0);
    const [validMatch, setValidMatch] = useState(true);
    const [validMinimumLength, setValidMinimumLength] = useState(false);
    const [validNoAccountName, setValidNoAccountName] = useState(true);
    const [validNoFullName, setValidNoFullName] = useState(true);
    const [validNotAllNumeric, setValidNotAllNumeric] = useState(true);
    const [validCountOfOthers, setValidCountOfOthers] = useState(false);
    const [validLowerCase, setValidLowerCase] = useState(false);
    const [validUpperCase, setValidUpperCase] = useState(false);
    const [validSpecialCharacter, setValidSpecialCharacter] = useState(false);
    const [validNumber, setValidNumber] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [hasError, setError] = useState(false);
    const [hasWarning, setWarning] = useState(false);
    const [isCardHidden, setCardHidden] = useState(false);
    const [isSuccess, setSuccess] = useState(false);
    const [input, setInput] = useState({
        password: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);

    const setNewPasswordForm = useForm({
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const { minimumLength, requiredOthersCount } = useMemo(() => {
        const parsedMinimumLength = (() => {
            if (passwordRules === undefined || passwordRules === '') {
                return minimumLengthDefault;
            }

            if (passwordRules.indexOf('|') < 0) {
                return minimumLengthDefault;
            }

            const rules = passwordRules.split('|');
            for (let i = 0; i < rules.length; i++) {
                if (rules[i].indexOf('=') < 0) {
                    continue;
                }

                const rule = rules[i].split('=');
                if (rule[0].toLowerCase() !== 'minimumlength') {
                    continue;
                }

                const parsed = Number(rule[1]);
                return Number.isFinite(parsed) ? parsed : minimumLengthDefault;
            }

            return minimumLengthDefault;
        })();

        const parsedRequiredOthersCount = (() => {
            const countMatch = passwordRules?.match(/CountOfOthers=(\d+)/i);
            if (!countMatch) {
                return countOfOthersDefault;
            }

            const parsed = Number(countMatch[1]);
            return Number.isFinite(parsed) ? parsed : countOfOthersDefault;
        })();

        return {
            minimumLength: parsedMinimumLength,
            requiredOthersCount: parsedRequiredOthersCount,
        };
    }, [passwordRules]);

    useEffect(() => {
        // Keep focus on new password for server-side issues, but not when values do not match.
        if (hasError || (hasWarning && validMatch)) {
            setNewPasswordForm.setFocus('password');
        }
    }, [hasError, hasWarning, validMatch]);

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setInput(prev => ({
            ...prev,
            [name]: value
        }));
        validateInput(event);
    };

    const validateInput = (e: { target: { name: string; value: string; }; }) => {
        let { name, value } = e.target;
        const password = name === "password" ? value : input.password;
        const confirmPassword = name === "confirmPassword" ? value : input.confirmPassword;

        const isMinimumLengthValid = MinimumLength(password);
        const isNoAccountNameValid = NoAccountName(password);
        const isNoFullNameValid = NoFullName(password);
        const isNotAllNumericValid = NotAllNumeric(password);
        const isMatchValid = password === confirmPassword;
        const countOfOthersValidation = CountOfOthers(password);
        const isValidCountOfOthers = countOfOthersValidation.isValid;
        const hasBothValues = password.length > 0 && confirmPassword.length > 0;

        setValidMinimumLength(isMinimumLengthValid);
        setValidNoAccountName(isNoAccountNameValid);
        setValidNoFullName(isNoFullNameValid);
        setValidNotAllNumeric(isNotAllNumericValid);
        setValidCountOfOthers(isValidCountOfOthers);
        setValidLowerCase(countOfOthersValidation.hasLowerCase);
        setValidUpperCase(countOfOthersValidation.hasUpperCase);
        setValidSpecialCharacter(countOfOthersValidation.hasSpecialCharacter);
        setValidNumber(countOfOthersValidation.hasNumber);
        setValidMatch(isMatchValid);
        setIsValid(
            hasBothValues
            && isMatchValid
            && isMinimumLengthValid
            && isNoAccountNameValid
            && isNoFullNameValid
            && isNotAllNumericValid
            && isValidCountOfOthers
        );

        switch (name) {
            case "password":
                // No-op: state updates are handled from recalculated values above.
                break;

            case "confirmPassword":
                // No-op: state updates are handled from recalculated values above.
                break;

            default:
                break;
        }
    }

    function MinimumLength(password: string) {
        if (password.length >= minimumLength) {
            return true;
        }

        return false;
    }

    function NoAccountName(password: string) {
        const userId = selectedAccount?.userId;
        if (!userId || password.toLowerCase().includes(userId.toLowerCase())) {
            return false;
        }

        return true;
    }

    function NoFullName(password: string) {
        if (password.toLowerCase().includes(user.firstName.toLowerCase()) || password.toLowerCase().includes(user.lastName.toLowerCase())) {
            return false;
        }

        return true;
    }

    function NotAllNumeric(password: string) {
        let numbers = /^[0-9]+$/;
        if (numbers.test(password)) {
            return false;
        }

        return true;
    }

    function CountOfOthers(password: string): CountOfOthersValidation {
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecialCharacter = /[^A-Za-z0-9]/.test(password);
        const satisfiedCount = [hasUpperCase, hasLowerCase, hasNumber, hasSpecialCharacter].filter(Boolean).length;
        const minimumRequired = Math.min(Math.max(requiredOthersCount, 0), 4);

        return {
            hasLowerCase,
            hasUpperCase,
            hasSpecialCharacter,
            hasNumber,
            isValid: minimumRequired <= 0 || satisfiedCount >= minimumRequired,
        };
    }

    const handlePasswordInputOnClear = () => {
        setInput({ password: '', confirmPassword: input.confirmPassword });
        setValidMatch(input.confirmPassword.length === 0);
        setValidMinimumLength(false);
        setValidNoAccountName(true);
        setValidNoFullName(true);
        setValidNotAllNumeric(true);
        setValidCountOfOthers(requiredOthersCount <= 0);
        setValidLowerCase(false);
        setValidUpperCase(false);
        setValidSpecialCharacter(false);
        setValidNumber(false);
        setIsValid(false);
    };

    const handleConfirmPasswordInputOnClear = () => {
        setInput({ password: input.password, confirmPassword: '' });
        setValidMatch(input.password.length === 0);
        setIsValid(false);
    };

    const changePasswordMutation = useChangePassword();

    const onNewPasswordSubmit = (data: { password: string; confirmPassword: string; }, e?: React.BaseSyntheticEvent) => {
        if (!selectedAccount) {
            // Should never happen: accounts should only be empty on an upstream API failure.
            console.error('NewPassword: submit attempted with no active account');
            setError(true);
            setWarning(false);
            return;
        }
        const submittedPassword = data.password ?? '';
        const submittedConfirmPassword = data.confirmPassword ?? input.confirmPassword;
        const isMinimumLengthValid = MinimumLength(submittedPassword);
        const isNoAccountNameValid = NoAccountName(submittedPassword);
        const isNoFullNameValid = NoFullName(submittedPassword);
        const isNotAllNumericValid = NotAllNumeric(submittedPassword);
        const countOfOthersValidation = CountOfOthers(submittedPassword);
        const isValidCountOfOthers = countOfOthersValidation.isValid;
        const isMatchValid = submittedPassword === submittedConfirmPassword;
        const hasBothValues = submittedPassword.length > 0 && submittedConfirmPassword.length > 0;
        const isFormValid = hasBothValues
            && isMatchValid
            && isMinimumLengthValid
            && isNoAccountNameValid
            && isNoFullNameValid
            && isNotAllNumericValid
            && isValidCountOfOthers;

        if (!isFormValid) {
            setValidMinimumLength(isMinimumLengthValid);
            setValidNoAccountName(isNoAccountNameValid);
            setValidNoFullName(isNoFullNameValid);
            setValidNotAllNumeric(isNotAllNumericValid);
            setValidCountOfOthers(isValidCountOfOthers);
            setValidLowerCase(countOfOthersValidation.hasLowerCase);
            setValidUpperCase(countOfOthersValidation.hasUpperCase);
            setValidSpecialCharacter(countOfOthersValidation.hasSpecialCharacter);
            setValidNumber(countOfOthersValidation.hasNumber);
            setValidMatch(isMatchValid);
            setIsValid(false);
            return;
        }

        setIsLoading(true);  //start spinner
        const params: PasswordParams = {
            password: btoa(data.password), //Encode the password with base64 encoding
            resourceId: selectedAccount.resourceId
        };
        changePasswordMutation.mutate(params,
            {
                onSuccess: (response) => {
                    setIsLoading(false);  //stop spinner
                    if (response.data.Response.toLowerCase() === "success") {
                        setError(false);
                        setWarning(false);
                        setCardHidden(true);
                        setSuccess(true);
                    } else if (response.data.Response.toLowerCase() === "invalidpassword") {
                        setError(false);
                        setWarning(true);
                    } else {
                        setError(true);
                        setWarning(false);
                    }
                },
                onError: () => {
                    setIsLoading(false);  //stop spinner
                    setError(true);
                    setWarning(false);
                },
            });
    };

    const resetState = () => {
        setNewPasswordForm.reset();
        setValidMatch(true);
        setValidMinimumLength(false);
        setValidNoAccountName(true);
        setValidNoFullName(true);
        setValidNotAllNumeric(true);
        setValidCountOfOthers(false);
        setValidLowerCase(false);
        setValidUpperCase(false);
        setValidSpecialCharacter(false);
        setValidNumber(false);
        setIsValid(false);
        setError(false);
        setWarning(false);
        setCardHidden(false);
        setSuccess(false);
        setInput({ password: '', confirmPassword: '' });
    };

    return (
        <Layout.Stack alignLayout="center">
            {hasWarning && <HelpMeAlert
                status="warning"
                title={t('newPasswordWarningPrompt')}
            >
                {warningBody}
            </HelpMeAlert>}

            {hasError && <HelpMeAlert
                status="error"
                title={t('newPasswordError1')}
            >
                {t('newPasswordError2')}
            </HelpMeAlert>}
            
            {!isCardHidden && <Card
                css={{
                    '&.abyss-card-root': {
                        border: '1px solid $gray4',
                        boxShadow: 'none',
                        borderRadius: `${cardBorderRadius}px`,
                        padding: `${cardPadding}px`,
                        margin: 4,
                    }
                }}
            >
                <div>
                    <div style={{ border: 0, padding: `${cardPadding}px`, margin: 4, }}>
                        <Layout.Stack alignLayout="center">
                            <Heading level={headingOffset} color="$textPrimary" data-testid="setNewPasswordHeading">{t('setNewPassword')}</Heading>
                            <ul>
                                <li style={{ textAlign: 'center', lineHeight: `${lineHeight}px` }}>
                                    <PasswordRulesPrompt data-testid="passwordRulesPrompt" fontWeight='medium' fontSize={textSize}>{t('passwordRulesPrompt')}</PasswordRulesPrompt>
                                </li>
                                <li style={{ lineHeight: `${lineHeight}px` }}>
                                    <RuleInlineIcon icon="circle" size={5} color="$textPrimary" />
                                    <Text data-testid="passwordRules1" fontWeight='medium' fontSize={textSize}>{t('passwordRules1', { count: requiredOthersCount })}</Text>
                                </li>
                                <li style={{ lineHeight: `${lineHeight}px` }}>
                                    <RuleInlineIcon icon="circle" size={5} color="$textPrimary" />
                                    <Text data-testid="passwordRules2" fontWeight='medium' fontSize={textSize}>{t('passwordRules2')}</Text>
                                </li>
                                <li style={{ lineHeight: `${lineHeight}px` }}>
                                    <RuleInlineIcon icon="circle" size={5} color="$textPrimary" />
                                    <Text data-testid="passwordRules3" fontWeight='medium' fontSize={textSize}>{t('passwordRules3')}</Text>
                                </li>
                                <li style={{ lineHeight: `${lineHeight}px` }}>
                                    <RuleInlineIcon icon="circle" size={5} color="$textPrimary" />
                                    <Text data-testid="passwordRules4" fontWeight='medium' fontSize={textSize}>{t('passwordRules4')}</Text>
                                </li>
                            </ul>
                            <LoadingSpinner isLoading={isLoading} ariaLoadingLabel="Changing password" />
                        </Layout.Stack>
                    </div>
                    <FormProvider state={setNewPasswordForm} onSubmit={onNewPasswordSubmit}>
                        <Layout.Stack alignLayout="center">
                            <SelectedAccountInfoBox
                                onSelect={resetState}
                            />

                            <PasswordInputBlock>
                                <PasswordTextInput
                                    label={t('newPassword')}
                                    hideLabel
                                    type="password"
                                    placeholder={t('newPassword')}
                                    model="password"
                                    isDisabled={isLoading}
                                    validators={{ required: true }}
                                    onChange={handlePasswordChange}
                                    isClearable
                                    onClear={handlePasswordInputOnClear}
                                    data-testid="passwordInput"
                                    width={`${textInputWidth}px`}
                                />
                            </PasswordInputBlock>
                            <ConfirmPasswordInputBlock>
                                <PasswordTextInput
                                    label={t('confirmedNewPassword')}
                                    hideLabel
                                    type="password"
                                    placeholder={t('confirmedNewPassword')}
                                    model="confirmPassword"
                                    isDisabled={isLoading}
                                    validators={{ required: true }}
                                    onChange={handlePasswordChange}
                                    isClearable
                                    onClear={handleConfirmPasswordInputOnClear}
                                    data-testid="confirmPasswordInput"
                                    width={`${textInputWidth}px`}
                                />
                            </ConfirmPasswordInputBlock>
                            <ValidationList data-testid="validationList">
                                {!validMatch && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="close" size="sm" color="$statusError" />
                                        <Text data-testid="validationMatchRed" color="$statusError" fontWeight='medium' fontSize={textSize}>{t('validationMatch')}</Text>
                                    </li>
                                )}
                                {!validMinimumLength && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="close" size="sm" color="$statusError" />
                                        <Text data-testid="validationMinLengthRed" color="$statusError" fontWeight='medium' fontSize={textSize}>{t('validationMinLength', { minimum: minimumLength })}</Text>
                                    </li>
                                )}
                                {!validNoAccountName && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="close" size="sm" color="$statusError" />
                                        <Text data-testid="validationUserIdhRed" color="$statusError" fontWeight='medium' fontSize={textSize}>{t('validationUserId')}</Text>
                                    </li>
                                )}
                                {!validNoFullName && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="close" size="sm" color="$statusError" />
                                        <Text data-testid="validationNameRed" color="$statusError" fontWeight='medium' fontSize={textSize}>{t('validationName')}</Text>
                                    </li>
                                )}
                                {!validNotAllNumeric && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="close" size="sm" color="$statusError" />
                                        <Text data-testid="validationNumericRed" color="$statusError" fontWeight='medium' fontSize={textSize}>{t('validationNumeric')}</Text>
                                    </li>
                                )}
                                {requiredOthersCount > 0 && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon={validCountOfOthers ? 'check' : 'close'} size="sm" color={validCountOfOthers ? '$statusSuccess' : '$statusError'} />
                                        <Text data-testid={validCountOfOthers ? 'validationCountOfOthersGreen' : 'validationCountOfOthersRed'} color={validCountOfOthers ? '$statusSuccess' : '$statusError'} fontWeight='medium' fontSize={textSize}>{t('validationCountOfOthers', { count: requiredOthersCount })}</Text>
                                        <ul style={{ listStyleType: 'none', margin: '4px 0 0 24px', paddingLeft: 0 }}>
                                            <li style={{ lineHeight: `${lineHeight}px` }}>
                                                <ValidationInlineIcon icon={validLowerCase ? 'check' : 'close'} size="sm" color={validLowerCase ? '$statusSuccess' : '$statusError'} />
                                                <Text data-testid={validLowerCase ? 'validationLowerCaseGreen' : 'validationLowerCaseRed'} color={validLowerCase ? '$statusSuccess' : '$statusError'} fontWeight='medium' fontSize={textSize}>{t('validationLowerCase')}</Text>
                                            </li>
                                            <li style={{ lineHeight: `${lineHeight}px` }}>
                                                <ValidationInlineIcon icon={validUpperCase ? 'check' : 'close'} size="sm" color={validUpperCase ? '$statusSuccess' : '$statusError'} />
                                                <Text data-testid={validUpperCase ? 'validationUpperCaseGreen' : 'validationUpperCaseRed'} color={validUpperCase ? '$statusSuccess' : '$statusError'} fontWeight='medium' fontSize={textSize}>{t('validationUpperCase')}</Text>
                                            </li>
                                            <li style={{ lineHeight: `${lineHeight}px` }}>
                                                <ValidationInlineIcon icon={validSpecialCharacter ? 'check' : 'close'} size="sm" color={validSpecialCharacter ? '$statusSuccess' : '$statusError'} />
                                                <Text data-testid={validSpecialCharacter ? 'validationSpecialCharacterGreen' : 'validationSpecialCharacterRed'} color={validSpecialCharacter ? '$statusSuccess' : '$statusError'} fontWeight='medium' fontSize={textSize}>{t('validationSpecialCharacter')}</Text>
                                            </li>
                                            <li style={{ lineHeight: `${lineHeight}px` }}>
                                                <ValidationInlineIcon icon={validNumber ? 'check' : 'close'} size="sm" color={validNumber ? '$statusSuccess' : '$statusError'} />
                                                <Text data-testid={validNumber ? 'validationNumberGreen' : 'validationNumberRed'} color={validNumber ? '$statusSuccess' : '$statusError'} fontWeight='medium' fontSize={textSize}>{t('validationNumber')}</Text>
                                            </li>
                                        </ul>
                                    </li>
                                )}
                                {validMinimumLength && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="check" size="sm" color="$statusSuccess" />
                                        <Text data-testid="validationMinLengthGreen" color="$statusSuccess" fontWeight='medium' fontSize={textSize}>{t('validationMinLength', { minimum: minimumLength })}</Text>
                                    </li>
                                )}
                                {validNoAccountName && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="check" size="sm" color="$statusSuccess" />
                                        <Text data-testid="validationUserIdGreen" color="$statusSuccess" fontWeight='medium' fontSize={textSize}>{t('validationUserId')}</Text>
                                    </li>
                                )}
                                {validNoFullName && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="check" size="sm" color="$statusSuccess" />
                                        <Text data-testid="validationNameGreen" color="$statusSuccess" fontWeight='medium' fontSize={textSize}>{t('validationName')}</Text>
                                    </li>
                                )}
                                {validNotAllNumeric && (
                                    <li style={{ lineHeight: `${lineHeight}px` }}>
                                        <ValidationInlineIcon icon="check" size="sm" color="$statusSuccess" />
                                        <Text data-testid="validationNumericGreen" color="$statusSuccess" fontWeight='medium' fontSize={textSize}>{t('validationNumeric')}</Text>
                                    </li>
                                )}
                            </ValidationList>
                            <SubmitButtonBlock>
                                <SubmitButton
                                    type="submit"
                                    isDisabled={isLoading || !isValid}
                                    size="$md"
                                data-testid="submitButton">{t('submit')}</SubmitButton>
                            </SubmitButtonBlock>
                        </Layout.Stack>
                    </FormProvider>
                </div>
            </Card>}

            {isSuccess && <ResultPanel
                messageId="passwordSetMsg"
                message={t('passwordSetMsg')}
                icon="check_circle"
                iconColor="$statusSuccess"
                linkId="goHomeLink"
                linkTo="/"
                linkText={t('goHome')}
            />}
        </Layout.Stack>
    );
}
